import { useEffect, useState } from "react";
import { BellRing, Mail, Plus, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

export default function AlertSettings() {
  const [s, setS] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/admin/alerts").then((r) => setS(r.data)).catch((e) => toast.error(formatErr(e))); }, []);
  if (!s) return <p className="mt-6 text-sm text-[#5C6B63]">Loading…</p>;

  const add = () => {
    const v = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return toast.error("Enter a valid email");
    if (!s.recipients.includes(v)) setS({ ...s, recipients: [...s.recipients, v] });
    setEmail("");
  };
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); toast.success(ok(r)); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(""); }
  };
  const save = () => run("save", async () => { const { data } = await api.put("/admin/alerts", s); setS(data); }, () => "Alert settings saved");

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1fr]" data-testid="alerts-admin">
      <section className="bg-white ring-1 ring-black/5 rounded-2xl p-6 space-y-4">
        <h3 className="font-serif text-xl text-[#1E3A2B] flex items-center gap-2"><Mail size={18}/> Who gets alerts</h3>
        <div className="flex flex-wrap gap-2" data-testid="alerts-recipients">
          {s.recipients.map((r) => (
            <span key={r} className="inline-flex items-center gap-1 rounded-full bg-[#F0E8DE] px-3 py-1 text-sm" data-testid={`alerts-recipient-${r}`}>
              {r}<button onClick={() => setS({ ...s, recipients: s.recipients.filter((x) => x !== r) })} aria-label={`Remove ${r}`} className="p-1 -mr-1" data-testid={`alerts-remove-${r}`}><X size={15}/></button>
            </span>
          ))}
          {s.recipients.length === 0 && <span className="text-sm text-[#C86D51]">No recipients, so no alerts will be sent.</span>}
        </div>
        <div className="flex gap-2">
          <input className="h-10 flex-1 rounded-md border border-[#E2DACF] px-3 text-sm" placeholder="team@jawaitrails.com" value={email}
            onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="alerts-email-input"/>
          <button onClick={add} className="jt-pill-outline text-sm" data-testid="alerts-add"><Plus size={14}/> Add</button>
        </div>
        <p className="text-xs text-[#5C6B63]">Up to 10 addresses. Every address gets every alert you switch on.</p>
        <div className="flex gap-2 flex-wrap pt-2">
          <button onClick={save} disabled={busy === "save"} className="jt-pill-primary text-sm disabled:opacity-50" data-testid="alerts-save">Save settings</button>
          <button onClick={() => run("test", async () => { await api.put("/admin/alerts", s); return api.post("/admin/alerts/test"); }, (r) => `Test sent to ${r.data.sent_to.join(", ")}`)} disabled={!!busy} className="jt-pill-outline text-sm" data-testid="alerts-test"><Send size={13}/> Send test</button>
          <button onClick={() => run("sum", async () => { await api.put("/admin/alerts", s); return api.post("/admin/alerts/summary-now"); }, () => "Daily summary sent")} disabled={!!busy} className="jt-pill-outline text-sm" data-testid="alerts-summary-now">Send today&apos;s summary now</button>
        </div>
        <p className="text-[11px] text-[#5C6B63]">Send test and Send summary save your changes first.</p>
      </section>
      <section className="bg-white ring-1 ring-black/5 rounded-2xl p-6 space-y-3">
        <h3 className="font-serif text-xl text-[#1E3A2B] flex items-center gap-2"><BellRing size={18}/> Which alerts</h3>
        {Object.entries(s.labels).map(([k, label]) => (
          <label key={k} className="flex items-center justify-between gap-3 py-2 border-b border-[#F0E8DE] text-sm">
            <span>{label}</span>
            <Switch checked={!!s.events[k]} onCheckedChange={(v) => setS({ ...s, events: { ...s.events, [k]: v } })} data-testid={`alerts-event-${k}`}/>
          </label>
        ))}
        <label className="flex items-center justify-between gap-3 py-2 text-sm">
          <span>Daily summary at 7 PM IST<span className="block text-xs text-[#5C6B63]">Tomorrow&apos;s bookings, jeeps used per session, pending payments, open requests</span></span>
          <Switch checked={s.daily_summary} onCheckedChange={(v) => setS({ ...s, daily_summary: v })} data-testid="alerts-daily"/>
        </label>
      </section>
    </div>
  );
}
